import { createContext, useContext, useMemo, type ReactNode } from 'react'

import { FLAG_METADATA, type ClientFlagKey } from '@bettermode/flags/registry'

/**
 * Client-side feature-flag snapshot.
 *
 * The browser never talks to PostHog directly (no `posthog-js`, honoring the
 * no-member-tracking contract). Instead the server resolves the client-consumed
 * flags during SSR — inside the per-request `AsyncLocalStorage` evaluation
 * context — and serializes them into the page via `__BM_DATA__`
 * (`InjectedAppProps.flags`). This provider exposes that immutable snapshot to
 * the React tree, and the per-flag `client/featureFlags/hooks/*` read it via
 * `useFlag`.
 *
 * The legacy react-sdk `<OpenFeatureProvider>` still wraps the tree but is no
 * longer the source for these hooks; its removal lands with the cutover (T6).
 */
export type FlagsSnapshot = Record<string, boolean>

const FlagsContext = createContext<FlagsSnapshot>({})

interface FlagsProviderProps {
  flags: FlagsSnapshot | undefined
  children: ReactNode
}

export const FlagsProvider = ({ flags, children }: FlagsProviderProps) => {
  const snapshot = useMemo<FlagsSnapshot>(() => flags ?? {}, [flags])
  return (
    <FlagsContext.Provider value={snapshot}>{children}</FlagsContext.Provider>
  )
}

/**
 * Read a `client`-tagged boolean flag from the SSR-injected snapshot. When the
 * key is absent (no PostHog env, or the flag was not injected) it falls back to
 * the flag's registry-wide `defaultValue` — baked in at codegen from its
 * `default-on` / `default-off` tag — so the default is never hand-coded at the
 * call site. `key` is a `ClientFlagKey`, so only flags delivered to the browser
 * can be read here.
 */
export const useFlag = (key: ClientFlagKey): boolean => {
  const flags = useContext(FlagsContext)
  // `?.` is belt-and-braces: `ClientFlagKey` and `FLAG_METADATA` come from the
  // same generated file, so a key without metadata should be impossible — but a
  // stale bundle pairing an old registry with new code shouldn't crash a render.
  return flags[key] ?? FLAG_METADATA[key]?.defaultValue ?? false
}
